import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { defaultFormValues } from '../../defaultFormValues';
import type { AddTripFormValues } from '../../FormValues';
import { allowedSpaceTypeOptions } from '../allowedSpaceTypeOptions';
import { AdditionalPassengersContextField } from './AdditionalPassengersContextField';
import DoNotMultiloadField from './DoNotMultiloadField';
import HasInfectiousDiseaseField from './HasInfectiousDiseaseField';
import { NumberAttendantsContextField } from './NumberAttendantsContextField';
import { OxygenLitersPerMinField } from './OxygenLitersPerMinField';
import { OxygenRequiredField } from './OxygenRequiredField';
import { SeatEquipmentSelect } from './SeatEquipmentSelect';
import { useOrgSettingsDefaultValues } from './ServiceDetailsFormBodyHelpers';
import { WheelchairRequiredField } from './WheelchairRequiredField';
import { useOrganizationSettingsRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { emptyHeaders } from 'src/common/external/bambi-api/bambiBaseQuery';
import { ServiceTypeField } from 'src/common/ServiceTypeField';
import { SpaceTypeField } from 'src/common/SpaceTypeField';
import { useFormValues } from 'src/common/useFormValues';

export const serviceDetailsNameRoot = 'service_details';

export function ServiceDetailsFormBody() {
  const formContext = useFormContext();
  const { setValue, clearErrors } = formContext;

  // TODO: This would probably be more appropriate at the root form level where we set defaults
  // ex: assignDefaultValues(orgSettings) or something
  // We also don't really have a loading ux for this, which I didn't add here
  // That's not really an issue because these are probably loaded before the form is rendered
  // Except in tests, which is why I added the fake loader
  const { isLoading, isEditing } = useOrgSettingsDefaultValues(setValue);

  const formValues = useFormValues<AddTripFormValues>();

  const { data: orgSettings } =
    useOrganizationSettingsRetrieveQuery(emptyHeaders);
  const offeredSpaceTypes = orgSettings?.trip_space_types;
  const currentSpaceType = formValues.service_details.space_type;
  // Only an existing trip keeps an unoffered space type, so editing it doesn't
  // blank the value. New trips are limited to what the org offers.
  const spaceTypeOptions = useMemo(
    () =>
      allowedSpaceTypeOptions(
        offeredSpaceTypes,
        isEditing ? currentSpaceType : undefined
      ),
    [offeredSpaceTypes, isEditing, currentSpaceType]
  );

  // A new trip starts on the form's hardcoded default (or a value picked before
  // the org settings loaded), which the org may not offer. Move it to the first
  // offered type. The org's default, when set, is always offered.
  useEffect(() => {
    if (isEditing || !offeredSpaceTypes?.length) return;
    if (!offeredSpaceTypes.includes(currentSpaceType)) {
      setValue(
        `${serviceDetailsNameRoot}.space_type`,
        spaceTypeOptions[0].value
      );
    }
  }, [
    isEditing,
    offeredSpaceTypes,
    currentSpaceType,
    spaceTypeOptions,
    setValue,
  ]);

  useEffect(() => {
    if (!formValues.service_details.is_oxygen_required) {
      setValue(
        `${serviceDetailsNameRoot}.oxygen_liters_per_min`,
        defaultFormValues.service_details.oxygen_liters_per_min
      );
      clearErrors(`${serviceDetailsNameRoot}.oxygen_liters_per_min`);
    }
  }, [formValues.service_details.is_oxygen_required, clearErrors, setValue]);

  return (
    <>
      <div className="flex flex-wrap justify-start gap-4">
        <div className="max-w-xs grow">
          {isLoading && (
            <div
              className="hidden"
              data-testid="fake-loader-for-org-settings-in-tests"
            >
              Loading...
            </div>
          )}
          <SpaceTypeField
            fieldName={`${serviceDetailsNameRoot}.space_type`}
            options={spaceTypeOptions}
          />
        </div>
        <div className="max-w-xs grow">
          <ServiceTypeField
            fieldName={`${serviceDetailsNameRoot}.service_type`}
          />
        </div>
        <div className="max-w-xs grow">
          <SeatEquipmentSelect />
        </div>
        <div>
          <WheelchairRequiredField />
        </div>
        <div>
          <NumberAttendantsContextField />
        </div>
        <div>
          <AdditionalPassengersContextField />
        </div>
        <div>
          <HasInfectiousDiseaseField />
        </div>
        <div>
          <OxygenRequiredField />
        </div>
        {formValues.service_details.is_oxygen_required && (
          <div>
            <OxygenLitersPerMinField />
          </div>
        )}
        <div>
          <DoNotMultiloadField />
        </div>
      </div>
    </>
  );
}
